import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { premiumProviders, standardProviders } from '../../mocks/premiumProviders';

function StarRating({ rating, reviewCount }: { rating: number; reviewCount: number }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <i
            key={i}
            className={`text-sm ${
              i < fullStars
                ? 'ri-star-fill text-orange-500'
                : i === fullStars && hasHalf
                ? 'ri-star-half-fill text-orange-500'
                : 'ri-star-line text-gray-300'
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-gray-800">{rating}</span>
      <span className="text-xs text-gray-500">({reviewCount} reseñas)</span>
    </div>
  );
}

function PremiumCard({ provider }: { provider: typeof premiumProviders[0] }) {
  const [activeImg, setActiveImg] = useState(0);

  return (
    <div className="relative bg-white rounded-2xl overflow-hidden border border-orange-100 transition-all duration-300 hover:-translate-y-1"
      style={{ boxShadow: '0 8px 40px 0 rgba(245,124,0,0.13), 0 2px 12px 0 rgba(0,0,0,0.08)' }}
    >
      {/* Badge */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide"
        style={{ background: 'linear-gradient(135deg, #F57C00, #FF9800)', color: '#fff' }}
      >
        <i className="ri-medal-fill text-sm"></i>
        {provider.badge}
      </div>

      {/* Availability badge */}
      <div className={`absolute top-4 right-4 z-10 px-2.5 py-1 rounded-full text-xs font-semibold ${
        provider.available
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-500'
      }`}>
        {provider.available ? 'Disponible' : 'Ocupado'}
      </div>

      {/* Image carousel */}
      <div className="relative w-full h-64 overflow-hidden bg-gray-100">
        {provider.images.map((src, idx) => (
          <img
            key={idx}
            src={src}
            alt={`Trabajo de ${provider.name} ${idx + 1}`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
              idx === activeImg ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        {/* Carousel controls */}
        {provider.images.length > 1 && (
          <>
            <button
              onClick={() => setActiveImg((p) => (p - 1 + provider.images.length) % provider.images.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full cursor-pointer hover:bg-white transition-all"
            >
              <i className="ri-arrow-left-s-line text-gray-700 text-lg"></i>
            </button>
            <button
              onClick={() => setActiveImg((p) => (p + 1) % provider.images.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full cursor-pointer hover:bg-white transition-all"
            >
              <i className="ri-arrow-right-s-line text-gray-700 text-lg"></i>
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {provider.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`w-2 h-2 rounded-full cursor-pointer transition-all ${
                    idx === activeImg ? 'bg-orange-500 w-5' : 'bg-white/70'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Card content */}
      <div className="p-6">
        {/* Provider header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden border-2 border-orange-200">
            <img
              src={provider.avatar}
              alt={provider.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-gray-900 leading-tight">{provider.name}</h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-sm font-medium text-orange-600 bg-orange-50 px-2.5 py-0.5 rounded-full">
                {provider.category}
              </span>
              <span className="text-xs text-gray-500">
                <i className="ri-briefcase-line mr-1"></i>{provider.completedJobs} trabajos
              </span>
            </div>
          </div>
        </div>

        {/* Rating */}
        <div className="mb-3">
          <StarRating rating={provider.rating} reviewCount={provider.reviewCount} />
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
          {provider.description}
        </p>

        {/* Skills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {provider.skills.slice(0, 3).map((skill) => (
            <span
              key={skill}
              className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full"
            >
              {skill}
            </span>
          ))}
        </div>

        {/* Info row */}
        <div className="grid grid-cols-2 gap-3 mb-5 py-4 border-t border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Zona de servicio</p>
            <p className="text-xs font-medium text-gray-700 leading-snug">
              <i className="ri-map-pin-line text-orange-500 mr-1"></i>
              {provider.serviceArea}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Tiempo de respuesta</p>
            <p className="text-xs font-medium text-gray-700">
              <i className="ri-time-line text-orange-500 mr-1"></i>
              {provider.responseTime}
            </p>
          </div>
        </div>

        {/* Price + CTA */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-gray-400">Precio desde</p>
            <p className="text-lg font-bold text-orange-600">{provider.priceRange}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/proveedor/${provider.id}`}
              className="px-3 py-2 border border-orange-500 text-orange-600 text-xs font-semibold rounded-lg hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Ver perfil
            </Link>
            <Link
              to={`/solicitar-servicio/${provider.id}`}
              className="px-4 py-2 bg-orange-600 text-white text-xs font-semibold rounded-lg hover:bg-orange-700 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
            >
              Solicitar
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StandardCard({ provider }: { provider: typeof standardProviders[0] }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:-translate-y-0.5"
      style={{ boxShadow: '0 2px 12px 0 rgba(0,0,0,0.06)' }}
    >
      <div className="relative w-full h-40 overflow-hidden bg-gray-100">
        <img
          src={provider.image}
          alt={provider.name}
          className="w-full h-full object-cover object-top"
        />
        <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-semibold ${
          provider.available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {provider.available ? 'Disponible' : 'Ocupado'}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{provider.name}</h3>
            <span className="text-xs text-orange-600 font-medium">{provider.category}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <i className="ri-star-fill text-orange-500 text-sm"></i>
            <span className="text-sm font-semibold text-gray-800">{provider.rating}</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">{provider.description}</p>
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs text-gray-400">Desde</p>
            <p className="text-sm font-bold text-gray-800">${provider.basePrice} MXN</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Link
              to={`/proveedor/${provider.id}`}
              className="px-3 py-1.5 border border-orange-500 text-orange-600 text-xs font-semibold rounded-lg hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Ver perfil
            </Link>
            <Link
              to={`/solicitar-servicio/${provider.id}`}
              className="px-3 py-1.5 bg-orange-600 text-white text-xs font-semibold rounded-lg hover:bg-orange-700 transition-all cursor-pointer whitespace-nowrap"
            >
              Solicitar
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExplorePage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('categoria') || 'Todos';
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const categories = ['Todos', 'Electricidad', 'Plomería', 'Carpintería', 'Limpieza', 'Jardinería', 'Pintura', 'Aire Acondicionado', 'Cerrajería', 'Herrería'];

  useEffect(() => {
    const cat = searchParams.get('categoria');
    if (cat) setCategoryFilter(cat);
  }, [searchParams]);

  useEffect(() => {
    const catLabel = categoryFilter !== 'Todos' ? ` – ${categoryFilter}` : '';
    document.title = `Explorar Proveedores${catLabel} en Aguascalientes | Task Zone`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `Encuentra proveedores verificados de ${categoryFilter !== 'Todos' ? categoryFilter.toLowerCase() : 'plomería, electricidad, limpieza y más'} en Aguascalientes. Compara calificaciones, precios en MXN y solicita tu servicio de forma segura en Task Zone.`);

    const schemaId = 'schema-explore';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `Proveedores de Servicios en Aguascalientes${categoryFilter !== 'Todos' ? ` – ${categoryFilter}` : ''}`,
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/explorar${categoryFilter !== 'Todos' ? `?categoria=${encodeURIComponent(categoryFilter)}` : ''}`,
      description: `Directorio de proveedores verificados de servicios en Aguascalientes. Plomería, electricidad, limpieza, carpintería y más con precios en MXN.`,
      provider: {
        '@type': 'Organization',
        name: 'Task Zone',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [categoryFilter]);

  const filteredPremium = categoryFilter === 'Todos'
    ? premiumProviders
    : premiumProviders.filter((p) => p.category === categoryFilter);

  const filteredStandard = categoryFilter === 'Todos'
    ? standardProviders
    : standardProviders.filter((p) => p.category === categoryFilter);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Page Header */}
      <section className="bg-white border-b border-gray-100 pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-orange-600 transition-colors cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-800 font-medium">Explorar Servicios</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Explora Proveedores</h1>
              <p className="text-gray-500 text-lg">
                Encuentra el profesional ideal para tu servicio en Aguascalientes
              </p>
            </div>
            <div className="flex items-center gap-3 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
              <div className="w-9 h-9 flex items-center justify-center bg-orange-600 rounded-lg text-white">
                <i className="ri-map-pin-2-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500 leading-none">Ubicación</p>
                <p className="text-sm font-semibold text-gray-800 leading-snug">Aguascalientes, México</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl"
                style={{ background: 'linear-gradient(135deg, #F57C00, #FF9800)' }}
              >
                <i className="ri-medal-fill text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Proveedores Premium</h2>
                <p className="text-sm text-gray-500">Profesionales verificados con mayor experiencia y calificación</p>
              </div>
            </div>
            <div className="ml-auto hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-100 rounded-full">
              <i className="ri-shield-check-fill text-orange-500 text-sm"></i>
              <span className="text-xs text-orange-700 font-medium">Verificados por Task Zone</span>
            </div>
          </div>

          {filteredPremium.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {filteredPremium.map((provider) => (
                <PremiumCard key={provider.id} provider={provider} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-white rounded-2xl border border-gray-100">
              <p className="text-gray-400 text-sm">No hay proveedores premium en esta categoría.</p>
            </div>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="border-t border-dashed border-gray-200"></div>
      </div>

      {/* Standard Providers Section */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Otros proveedores</h2>
              <p className="text-sm text-gray-500">Explora más opciones disponibles en tu área</p>
            </div>
            {/* Category filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                    categoryFilter === cat
                      ? 'bg-orange-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filteredStandard.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStandard.map((provider) => (
                <StandardCard key={provider.id} provider={provider} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-sm">No hay proveedores disponibles en esta categoría por el momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 bg-orange-600 mt-4">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">¿Eres profesional de servicios?</h3>
          <p className="text-white/90 mb-6 text-base">
            Únete a Task Zone y conecta con clientes en Aguascalientes que necesitan tus habilidades.
          </p>
          <Link
            to="/registro-proveedor"
            className="inline-flex items-center gap-2 px-7 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            Registrarme como proveedor
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
